/**
 * useUnsavedChanges - Hook for tracking unsaved changes
 * Shows warning before leaving page with unsaved data
 */
import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Track unsaved changes and warn user before leaving
 * 
 * @param {object} currentData - Current form data
 * @param {object} originalData - Original/saved data (null for new)
 * @param {boolean} isEnabled - Enable/disable tracking
 * @returns {object} - { hasChanges, markAsSaved, isDirty }
 */
export function useUnsavedChanges(currentData, originalData = null, isEnabled = true) {
  const [savedData, setSavedData] = useState(originalData);
  const isEnabledRef = useRef(isEnabled);
  
  // Update ref when enabled changes
  useEffect(() => {
    isEnabledRef.current = isEnabled;
  }, [isEnabled]);
  
  // Check if data has changed
  const hasChanges = useCallback(() => {
    if (!isEnabledRef.current) return false;
    if (!savedData && !originalData) {
      // New item - check if any meaningful data entered
      return Object.values(currentData || {}).some(v => 
        v && (Array.isArray(v) ? v.length > 0 : String(v).trim().length > 0)
      );
    }
    return JSON.stringify(currentData) !== JSON.stringify(savedData || originalData);
  }, [currentData, savedData, originalData]);
  
  // Mark current state as saved
  const markAsSaved = useCallback(() => {
    setSavedData(currentData);
  }, [currentData]);
  
  // Reset to original
  const reset = useCallback(() => {
    setSavedData(originalData);
  }, [originalData]);
  
  // Setup beforeunload warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isEnabledRef.current && hasChanges()) {
        e.preventDefault();
        e.returnValue = 'Masz niezapisane zmiany. Czy na pewno chcesz opuścić stronę?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);
  
  return {
    hasChanges: hasChanges(),
    isDirty: hasChanges(),
    markAsSaved,
    reset
  };
}

/**
 * Auto-save draft to localStorage
 * 
 * @param {string} key - Storage key
 * @param {object} data - Data to save
 * @param {number} debounceMs - Debounce delay
 */
export function useAutosaveDraft(key, data, debounceMs = 2000) {
  const [lastSaved, setLastSaved] = useState(null);
  const timeoutRef = useRef(null);
  
  // Load draft on mount
  const loadDraft = useCallback(() => {
    try {
      const saved = localStorage.getItem(`draft_${key}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        return { data: parsed.data, savedAt: parsed.savedAt };
      }
    } catch (e) {
      console.warn('Failed to load draft:', e);
    }
    return null;
  }, [key]);
  
  // Save draft with debounce
  useEffect(() => {
    if (!data || !key) return;
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      try {
        const saveData = {
          data,
          savedAt: new Date().toISOString()
        };
        localStorage.setItem(`draft_${key}`, JSON.stringify(saveData));
        setLastSaved(saveData.savedAt);
      } catch (e) {
        console.warn('Failed to save draft:', e);
      }
    }, debounceMs);
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, key, debounceMs]);
  
  // Clear draft
  const clearDraft = useCallback(() => {
    localStorage.removeItem(`draft_${key}`);
    setLastSaved(null);
  }, [key]);
  
  return {
    loadDraft,
    clearDraft,
    lastSaved
  };
}

export default useUnsavedChanges;
